import isEmpty from 'lodash/isEmpty';

import React, { useState, useEffect } from 'react';
import InfiniteScroll from 'react-infinite-scroll-component';
import styled from 'styled-components';

import { useAppDispatch } from '../../../../hooks/useAppDispatcher.hook';

import SpinnerComponent from '../../../../components/Spinner.component';
import { ControlSectionTypes } from '../../../../redux/ControlPanel/ControlPanelList/ControlPanelList.type';

import { truncateText } from '../../../../utils/textTruncate';

const TEXT_MAX_LENGTH = 50;

const StyledContentWrapper = styled.div`
  height: 288px;
  overflow-y: scroll;
  margin-top: 10px;
`;

const TruncatedText = styled.span<{ $isTruncated?: boolean }>`
  ${({ $isTruncated }) => $isTruncated && `
    cursor: help;
  `}
`;

interface ControlSectionContentProps {
  isLoadMode: boolean;
  sectionType: ControlSectionTypes;
  initialData: any[];
  fetchData: Function;
  fetchParams: { q?: string; page: number; sort?: string };
  updateItems: Function;
  renderItem: (item: any) => JSX.Element;
  textMaxLength?: number;
  autoTruncate?: boolean;
}

const ControlSectionContent = ({
   isLoadMode = true,
   sectionType,
   initialData,
   fetchData,
   fetchParams,
   updateItems,
   renderItem,
   textMaxLength,
   autoTruncate = true,
 }: ControlSectionContentProps) => {
  const [hasMore, setHasMore] = useState<boolean>(isLoadMode);
  const [items, setItems] = useState<any[]>([]);
  const dispatch = useAppDispatch();

  const maxLength = textMaxLength || TEXT_MAX_LENGTH;

  useEffect(() => {
    if (isEmpty(items) || fetchParams.page === 1) {
      setItems(initialData);
      updateItems(initialData);
    }
  }, [initialData, fetchParams]);

  useEffect(() => {
    if (fetchParams.page === 1) setHasMore(!isEmpty(initialData));
  }, [initialData, fetchParams]);

  const renderItemWrapper = (item: any) => {
    if (!autoTruncate) {
      return renderItem(item);
    }

    const itemToRender = { ...item };

    if (item.name) {
      const displayText = truncateText(item.name, maxLength);
      const isTruncated = item.name.length > maxLength;

      itemToRender.truncatedDisplayName = (
        <TruncatedText
          title={isTruncated ? item.name : undefined}
          $isTruncated={isTruncated}
        >
          {displayText}
        </TruncatedText>
      );
    }

    return renderItem(itemToRender);
  };

  const fetchItems = async () => {
    const {
      payload: { data, pagination },
    } = await dispatch(
      fetchData({
        ...fetchParams,
        page: fetchParams.page + 1,
      }),
    );

    setHasMore(pagination.current_page < pagination.last_page);
    setItems(prev => {
      return [...prev, ...data];
    });
    updateItems([
      ...items,
      ...data.map((item: any) => ({
        id: item.id,
        name: item.name,
      })),
    ]);
  };

  return (
    <StyledContentWrapper id={`scrollableDiv-${sectionType}`}>
      <InfiniteScroll
        dataLength={items.length}
        next={fetchItems}
        hasMore={hasMore && isLoadMode}
        loader={<SpinnerComponent size="small" />}
        scrollableTarget={`scrollableDiv-${sectionType}`}
      >
        {items.map(item => renderItemWrapper(item))}
      </InfiniteScroll>
    </StyledContentWrapper>
  );
};

export default ControlSectionContent;
